// Port of SuiteModel/SpellingCandidates.swift: the words the Revise room's spelling check puts to a spelling checker —
// words of letters (an apostrophe or two inside), each part of a hyphenated word on its own, a possessive's ’s or a
// plural's ’ taken off. Not a name (a capital mid-sentence, or at a sentence's start when the book capitalises it
// mid-sentence elsewhere), a word in capitals, one with a digit, a single letter, anything in a link's syntax, a span's
// classes or a fence, a word with a letter dropped (`chasin’`, `’til`: dialect, on purpose), nor a word the writer knows
// (`known`: the glossary, the cast's names, the personal dictionary). A word used three times or more in the book is the
// writer's own, not a slip. Exact.
import { graphemes } from '../unicode.ts';
import { charIsUppercase, isAlphabetic, toScalars } from '../text/scalars.ts';
import { scan, startsSentence } from './variants.ts';

export interface SpellingWord {
  /** Which text (scene) it's in, and where (Unicode scalars). */
  text: number;
  range: [number, number];
  word: string;
}

/** How many times a word may be used in the book and still be checked as a slip. */
export const SPELLING_MOST_USES = 2;

const apostrophe = (c: string | undefined) => c === '’' || c === "'";
const isLetterChar = (c: string) => /^\p{L}/u.test(c);

/** `SpellingCandidates.find`: the words to check, in reading order, and how often each (lowercased) is used in the book. */
export function spellingCandidates(texts: readonly string[], known: Iterable<string> = []): { words: SpellingWord[]; uses: Map<string, number> } {
  const knownLower = new Set([...known].map((k) => k.toLowerCase()));
  const words: SpellingWord[] = [];
  const uses = new Map<string, number>();
  /** Capitalised words written mid-sentence somewhere: names, wherever they are. */
  const names = new Set<string>();
  texts.forEach((text, t) => {
    const sc = scan(toScalars(text));
    const s = sc.s;
    for (const token of sc.tokens) {
      const parts: { range: [number, number]; word: string }[] = [];
      let start = token.range[0];
      for (let i = token.range[0]; i <= token.range[1]; i++) {
        if (!(i === token.range[1] || s[i] === '-')) continue;
        let end = i;
        if (end - start > 2 && apostrophe(s[end - 2]) && (s[end - 1] === 's' || s[end - 1] === 'S')) end -= 2;
        const droppedAtStart = start > 0 && apostrophe(s[start - 1]) && (start === 1 || !isAlphabetic(s[start - 2]));
        const droppedAtEnd = end < s.length && apostrophe(s[end]) && !(end > start && (s[end - 1] === 's' || s[end - 1] === 'S'));
        if (end > start && !droppedAtStart && !droppedAtEnd) parts.push({ range: [start, end], word: s.slice(start, end).join('') });
        start = i + 1;
      }
      for (const part of parts) {
        const w = part.word;
        const chars = graphemes(w);
        if (chars.length < 2 || !chars.every((c) => isLetterChar(c) || apostrophe(c))) continue;
        const lower = w.toLowerCase();
        uses.set(lower, (uses.get(lower) ?? 0) + 1);
        if (knownLower.has(lower)) continue;
        if (w === w.toUpperCase()) continue;
        if (charIsUppercase(chars[0]) && !startsSentence(s, part.range[0])) {
          names.add(w);
          continue;
        }
        words.push({ text: t, range: part.range, word: w });
      }
    }
  });
  return { words: words.filter((w) => (uses.get(w.word.toLowerCase()) ?? 0) <= SPELLING_MOST_USES && !names.has(w.word)), uses };
}
